import {NameWithAvatar} from '@common/datatable/column-templates/name-with-avatar';
import React from 'react';
import {Track} from '@app/web-player/tracks/track';
import clsx from 'clsx';
import {useTrackTableMeta} from '@app/web-player/tracks/track-table/use-track-table-meta';
import {getTrackImageSrc} from '@app/web-player/tracks/track-image/track-image';
import {ArtistLinks} from '@app/web-player/artists/artist-links';
import {useIsTrackCued} from '@app/web-player/tracks/hooks/use-is-track-cued';
import {FormattedDate} from '@ui/i18n/formatted-date';
import { ChipList } from '@ui/forms/input-field/chip-field/chip-list';
import { Chip } from '@ui/forms/input-field/chip-field/chip';
import { Trans } from '@ui/i18n/trans';
import { BulletSeparatedItems } from '@app/web-player/layout/bullet-separated-items';
import { useIsMobileMediaQuery } from '@ui/utils/hooks/is-mobile-media-query';

interface TrackNameColumnProps {
  track: Track;
  rowIndex: number;
}
export function TrackNameColumn({track, rowIndex}: TrackNameColumnProps) {
  const {
    hideTrackImage,
    queueGroupId,
    resolveQueueGroupId,
    showArtistsInDescription,
    hideUnreleasedDateInDescription,
    openInDialogTrigger,
    closePlayerOverlayOnOpen,
  } = useTrackTableMeta();
  const resolvedQueueGroupId =
    resolveQueueGroupId?.(track, rowIndex) ?? queueGroupId;
  const isCued = useIsTrackCued(track.id, resolvedQueueGroupId);
  const isMobile = useIsMobileMediaQuery();
  const shouldShowArtists = showArtistsInDescription || isMobile;
  const shouldShowReleaseDate =
    !hideUnreleasedDateInDescription && !track.isReleased;
  
  return (
    <NameWithAvatar
      image={!hideTrackImage ? getTrackImageSrc(track) : undefined}
      label={track.name}
      isAvailable={track.isAvailable}
      isEncoded={track.isEncoded}
      avatarSize="w-40 h-40 md:w-32 md:h-32"
      description={
        <BulletSeparatedItems className="text-sm text-muted flex">
          {shouldShowReleaseDate && (
            <div><Trans message="Out on" /> <FormattedDate date={track.releaseDate} /></div>
          )}
          {shouldShowArtists && (
            <ArtistLinks
              className="max-w-[200px] truncate ellipsis"
              artists={track.artists}
              openInDialogTrigger={openInDialogTrigger}
              closePlayerOverlayOnOpen={closePlayerOverlayOnOpen}
            />
          )}
          {(!track.isAvailable || !track.isEncoded) && (
            <Badges isAvailable={track.isAvailable} isEncoded={track.isEncoded} isExclusivity={track.isExclusivity} />
          )}
        </BulletSeparatedItems>
      }
      labelClassName={clsx(
        isCued && 'text-primary',
        'max-md:text-[15px] max-md:leading-6',
      )}
    />
  );
}

interface BadgeProps {
  isAvailable?: boolean;
  isEncoded?: boolean;
  isExclusivity?: boolean;
}
function Badges({isAvailable = true, isEncoded = true, isExclusivity = false}: BadgeProps) {
  return (
    <ChipList size="xxs" color="positive" radius='rounded-full'>
      {!isAvailable ? (
        <Chip color='danger'>
          <span className={clsx('capitalize')}><Trans message="Unavailable" /></span>
        </Chip>
      ) : <></>}
      {!isEncoded ? (
        <Chip color="danger">
          <span className={clsx('capitalize')}><Trans message="Unencoded" /></span>
        </Chip>
      ) : <></>}
      {isExclusivity ? (
        <Chip color="primary">
          <span className={clsx('capitalize')}><Trans message="Exclusive" /></span>
        </Chip>
      ) : <></>}
    </ChipList>
  )
}
